// =============================================================================
// io/share/capture.ts — turning the report on screen into a frozen payload.
// -----------------------------------------------------------------------------
// Relocated from the old client's `_shareRegion` capture half and `_shareMeasure`
// (app/index.html:29380-29470).
//
// ── WHY THE RENDERED HTML AND NOT THE STATE ──
//
// A share stores the ALREADY-RENDERED report as static markup. The visitor sees
// exactly what the sharer saw — no state reconstruction, no second renderer to
// keep in step, and no way for a report to change meaning after it was sent.
//
// ⚠ THIS FILE IS `io/` BECAUSE IT TOUCHES THE DOM, and that is the whole reason
// the layering allows it here: `domain/` may not, and `ui/` must not do the
// arithmetic. The rules about WHAT may be published live in `domain/share.ts`;
// this file is only the mechanics of lifting it off the page.
// =============================================================================

import {
  SHARE_SHOT_MAX_BYTES, SHARE_SHOT_MAX_PER_TRADE, SHARE_SHOT_MAX_TOTAL,
  type ShareTrade,
} from '../../domain/share';

/**
 * Selectors removed from every capture, on top of `[data-noshare]`.
 *
 * ⚠ `.account-cell[title]` IS A DISCLOSURE FIX, not tidying. The trades table
 * puts the RAW broker account string in a `title` attribute on every row — the
 * untouched string from the export — and a shared report would carry it in
 * plain text in view-source, where no hover is needed to read it. Only the
 * tooltip goes, and only in the copy that is published.
 */
const STRIP_TITLE = '.account-cell[title]';

/**
 * Grids that must collapse to one column once their second child is removed.
 *
 * ⚠ Removing the Prop Rails card takes the CONTENT but not the COLUMN. The row
 * is `2fr 1fr`, so the share rendered the intraday chart at two-thirds width
 * beside a large hole, and Josh reasonably asked what was missing.
 */
const COLLAPSE_GRIDS = '.dayview-intraday';

/**
 * The captured trades table, which is REMOVED rather than published.
 *
 * ⚠ It is re-rendered by the visitor's own page from the serialised trades, so
 * the rows can actually be CLICKED — inside the sandboxed frame nothing can bind
 * to them, and a captured copy sits there looking identical and doing nothing.
 */
const TRADES_TABLE = '.share-trades-table';

export interface CapturedRegion {
  html: string;
  /** The height of the STRIPPED clone at the width the visitor's frame uses. */
  contentHeight: number | null;
}

/**
 * Lift the report out of the page, minus everything a public copy must not
 * carry.
 *
 * ⚠ Works on a CLONE throughout. The live DOM is React's, and removing nodes
 * from it — even briefly — leaves React reconciling children it did not render.
 */
export function captureShareRegion(root: Element): CapturedRegion {
  const clone = root.cloneNode(true) as HTMLElement;

  // Interactive chrome and the four D213 exclusions, all marked at the source.
  clone.querySelectorAll('[data-noshare]').forEach((n) => { n.remove(); });
  clone.querySelectorAll(STRIP_TITLE).forEach((n) => { n.removeAttribute('title'); });
  clone.querySelectorAll(COLLAPSE_GRIDS).forEach((g) => {
    if (g.children.length <= 1) (g as HTMLElement).style.gridTemplateColumns = '1fr';
  });
  clone.querySelectorAll(TRADES_TABLE).forEach((n) => {
    const card = n.closest('.card') ?? n;
    const prev = card.previousElementSibling;
    // Its section divider goes with it, or the report ends on a heading over
    // nothing.
    if (prev?.classList.contains('an-section-divider')) prev.remove();
    card.remove();
  });

  return { html: clone.innerHTML, contentHeight: measureClone(clone) };
}

/**
 * Height of the stripped clone, at the width the visitor's frame will use.
 *
 * ⚠ IT HAS TO BE IN THE DOCUMENT TO HAVE A LAYOUT AT ALL — `scrollHeight` on a
 * detached node is 0, which is why the first version silently fell back to the
 * live tab's height. Parked off-screen rather than hidden, because
 * `display: none` also measures 0.
 *
 * ⚠ MEASURING THE LIVE REGION WOULD BE WRONG. The clone has had Prop Rails, By
 * Account, the rule chip, the plan/recap AND the whole trades table removed, so
 * the live tab is far taller than what is actually shared — and the difference
 * renders as a long empty band before the visitor's own trades table.
 */
function measureClone(clone: HTMLElement): number | null {
  try {
    const box = document.createElement('div');
    // The visitor's frame is the viewport width with content capped at 1480 by
    // the app's own layout — mirror that so wrapping matches.
    box.style.cssText =
      'position:absolute;left:-99999px;top:0;width:1480px;visibility:hidden;';
    box.className = 'tab-section';
    box.appendChild(clone.cloneNode(true));
    document.body.appendChild(box);
    const h = box.scrollHeight || 0;
    box.remove();
    return h || null;
  } catch {
    // A measurement we cannot take is a fallback the renderer already handles.
    return null;
  }
}

// ---------------------------------------------------------------------------
// Screenshots
// ---------------------------------------------------------------------------

export interface ShotSource {
  /** Filenames in the `screenshots` bucket, in the order the trade lists them. */
  screenshots: readonly string[];
}

export interface ShotCopyResult {
  copied: number;
  /** Reported, never silent — a capped share looks complete and is not. */
  skipped: number;
}

/**
 * Copy each trade's screenshots INTO the payload as data URLs.
 *
 * ⚠ IN THE PAYLOAD, NOT A PUBLIC BUCKET, and that is what makes revoke honest.
 * A public bucket would keep serving every copied image after a link was revoked
 * — to anyone who saved a URL, and to anyone who walked `{token}/1`,
 * `{token}/2`. Living inside the payload means the Edge Function's revocation
 * gate is the ONLY door, so revoking really does take the pictures back.
 *
 * ⚠ `sources` and `tds` are INDEX-ALIGNED. Both come from the same list in the
 * same order; a caller that filters one and not the other attaches one trade's
 * charts to another trade.
 */
export async function copyShotsIntoPayload(
  sources: readonly ShotSource[],
  tds: ShareTrade[],
  download: (filename: string) => Promise<Blob | null>,
): Promise<ShotCopyResult> {
  let copied = 0, bytes = 0, skipped = 0;
  const n = Math.min(sources.length, tds.length);

  for (let i = 0; i < n; i++) {
    const files = sources[i]?.screenshots ?? [];
    const shots: string[] = [];
    for (const f of files.slice(0, SHARE_SHOT_MAX_PER_TRADE)) {
      if (copied >= SHARE_SHOT_MAX_TOTAL || bytes >= SHARE_SHOT_MAX_BYTES) {
        skipped++;
        continue;
      }
      try {
        const blob = await download(f);
        if (!blob) { skipped++; continue; }
        const url = await blobToDataUrl(blob);
        shots.push(url);
        copied++;
        bytes += url.length;
      } catch {
        // ⚠ One unreadable image must not cost the whole share. It is counted,
        // and the count is shown.
        skipped++;
      }
    }
    const td = tds[i];
    if (td && shots.length) td.shots = shots;
  }
  return { copied, skipped };
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result ?? ''));
    r.onerror = () => reject(new Error('screenshot could not be read'));
    r.readAsDataURL(blob);
  });
}

/**
 * A share token: two UUIDs, hyphens removed — 256 bits of randomness.
 *
 * ⚠ THE TOKEN IS THE PASSWORD. It is the only thing between a URL and somebody's
 * trading record, so it is generated by the platform CSPRNG and never by
 * anything seeded, counted or derived from the report.
 */
export function newShareToken(): string {
  return (crypto.randomUUID() + crypto.randomUUID()).replace(/-/g, '');
}
